import { useEffect, useState } from "react";

// Constants
import {
  CityPdfReportConstants,
  FormDataTargets,
  LocationSummaryWithinConstants,
} from "../../constants/GeneralConstants";

// Components
import ChipsInput from "../chips-input/ChipsInput";
import { PlainDropdown } from "../dropdown/Dropdown";

function ReportSelectionSection({
  isMediaSitesPresent = false,
  selectedReport = "",
  setSelectedReport = () => {},
  radius,
  setRadius = () => {},
  isCityMediasSingleAndZipReport,
}) {
  const { cityPdf, cityMediasSinglePdf, cityMediasZip } =
    CityPdfReportConstants;

  const cityReportOptions = [
    {
      id: cityPdf,
      label: "City PDF",
      showOption: true,
    },
    {
      id: cityMediasSinglePdf,
      label: "City Medias Single PDF",
      showOption: isMediaSitesPresent,
    },
    {
      id: cityMediasZip,
      label: "City Medias Zip",
      // showOption: isMediaSitesPresent,
      showOption: false, // once doc-service server down issue fixed, make it enable
    },
  ];

  return (
    <div className="mb-4 row">
      <div className="col-6">
        <b>Select Report to Download</b>
        {cityReportOptions.map((option, i) => {
          const { id, label, showOption } = option;
          if (!showOption) {
            return;
          }
          return (
            <div key={i} className="d-flex align-items-center">
              <input
                type="radio"
                name="cityReport"
                id={id}
                className="cursor-pointer"
                onChange={() => setSelectedReport(id)}
                checked={selectedReport === id}
              />
              <label className="m-0 pl-2 mr-5" htmlFor={id}>
                {label}
              </label>
            </div>
          );
        })}
      </div>

      {/* Radius Selector */}
      {isCityMediasSingleAndZipReport && (
        <div className="col-6">
          <b>Select Radius For Media Location Summary</b>
          <PlainDropdown
            items={LocationSummaryWithinConstants}
            selectedId={radius}
            onItemSelect={(id) => setRadius(id)}
            className="border rounded-lg w-50 py-1 px-4"
            textClassName="text-primary"
          />
        </div>
      )}
    </div>
  );
}

function ReportDownloadMethods({
  isSentViaEmail,
  setIsSentViaEmail = () => {},
  isCityMediasSingleAndZipReport,
}) {
  // data
  const reportDownloadOptions = [
    {
      id: "sentViaEmail",
      label: "Sent Via Email",
      checked: isSentViaEmail,
    },
    {
      id: "download",
      label: "Local Download",
      checked: !isSentViaEmail,
    },
  ];

  // info for longer downloading time
  const isShowInfoMsg = isCityMediasSingleAndZipReport && !isSentViaEmail;

  return (
    <div className="mb-4 row">
      <div className="col-6">
        <b>Select PDF Download Option </b>
        {reportDownloadOptions.map((option, i) => {
          const { id, label, checked } = option;

          return (
            <div key={i} className="d-flex align-items-center">
              <input
                type="radio"
                name="reportDownloadOption"
                id={id}
                className="cursor-pointer"
                onChange={() => setIsSentViaEmail(!isSentViaEmail)}
                checked={checked}
              />
              <label className="m-0 pl-2 mr-5" htmlFor={id}>
                {label}
              </label>
            </div>
          );
        })}
      </div>

      {/* Info Message */}
      {isShowInfoMsg && (
        <div className="col-6 text-info">
          <li className="mb-0">{`Downloading "City Medias Single PDF"`}</li>
          <li className="mb-0">{`For higher number of media
            sites (i.e >50), it will take longer time to generate PDF (approx 5-7
            min).`}</li>
          <li className="mb-0">
            {`Either choose "Sent Via Email" option or wait for above
            mentioned time.`}
          </li>
        </div>
      )}
    </div>
  );
}

function EmailIdsSection({ emailIds = [], setEmailIds }) {
  return (
    <div className="mb-2">
      <b>Emails</b>
      <ChipsInput
        chipsAsEmail={true}
        initialChips={emailIds}
        handleChange={(ids) => setEmailIds(ids)}
      />
    </div>
  );
}

/**
 * Download Reports Form
 */
export default function DownloadReportsForm({
  buttonLabel = "Confirm",
  handleSubmit = () => {},
  handleClose = () => {},

  // options for city reports
  isCityReportForm = false,
  isMediaSitesPresent = false,
}) {
  const [isSentViaEmail, setIsSentViaEmail] = useState(true);
  const [emailIds, setEmailIds] = useState([]);
  const [selectedReport, setSelectedReport] = useState("");
  const [radius, setRadius] = useState(1000);

  // reset
  useEffect(() => {
    setIsSentViaEmail(true);
    setEmailIds([]);
    setSelectedReport(CityPdfReportConstants.cityPdf);
    setRadius(1000);
  }, []);

  // submit btn disabled
  const isSubmitDisabled =
    (isSentViaEmail && emailIds.length === 0) || // emailIds only needed when sending reports via emails
    (isCityReportForm && !selectedReport); // for city report, type of report should be selected to download

  // functions
  function handleSubmitModal() {
    // handle submit
    handleSubmit(isSentViaEmail, emailIds, selectedReport, radius);

    // handle close
    handleClose();
  }

  // checking cityMedias report selected or not
  const { cityMediasSinglePdf, cityMediasZip } = CityPdfReportConstants;
  const isCityMediasSingleAndZipReport =
    selectedReport === cityMediasSinglePdf || selectedReport === cityMediasZip;

  return (
    <div
      className="modal fade"
      id={FormDataTargets.downloadReportsForm}
      data-backdrop="static"
    >
      <div className="modal-dialog modal-lg" role="document">
        <div className="modal-content">
          {/* Form Header */}
          <div className="modal-header bg-alt px-5">
            <h4 className="modal-title">{"Download Report"}</h4>
          </div>

          {/* Form body */}
          <div className="modal-body px-5 py-4">
            {/* report selection section */}
            {isCityReportForm && (
              <ReportSelectionSection
                isMediaSitesPresent={isMediaSitesPresent}
                selectedReport={selectedReport}
                setSelectedReport={setSelectedReport}
                radius={radius}
                setRadius={setRadius}
                isCityMediasSingleAndZipReport={isCityMediasSingleAndZipReport}
              />
            )}

            {/* Report Download Methods */}
            <ReportDownloadMethods
              isSentViaEmail={isSentViaEmail}
              setIsSentViaEmail={setIsSentViaEmail}
              isCityMediasSingleAndZipReport={isCityMediasSingleAndZipReport}
            />

            {/* emails section : only show when "sentViaEmail" */}
            {isSentViaEmail && (
              <EmailIdsSection emailIds={emailIds} setEmailIds={setEmailIds} />
            )}
          </div>

          {/* Form Footer */}
          <div className="modal-footer border-0 px-5">
            <button
              type="button"
              className="btn btn-outline-primary btn-action mt-2 mr-3 rounded-lg shadow-none"
              data-dismiss="modal"
              onClick={() => handleClose()}
            >
              {"Cancel"}
            </button>
            <button
              type="button"
              className="btn btn-primary btn-action mt-2 rounded-lg shadow-none"
              data-dismiss="modal"
              disabled={isSubmitDisabled}
              onClick={handleSubmitModal}
            >
              {buttonLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
